import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, forwardRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'base';
import { ConfigurationService } from '../../services/configuration.service';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'common';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { Integration, IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DefaultIntegrationSettingsChild } from '../default-integration-settings-child-interface/default-integration-settings-child-interface';


@Component({
	selector: 'assetpanda-default-settings',
	templateUrl: './assetpanda-default-settings.component.html',
	styleUrls: ['./assetpanda-default-settings.component.scss']
})
export class AssetPandaDefaultSettingsComponent extends ComponentBase implements DefaultIntegrationSettingsChild, OnInit, OnDestroy, OnChanges {
	@Input()
	public inputSettings: Integration;

	public form: UntypedFormGroup;

	public enabled: UntypedFormControl;

	public serverURL: UntypedFormControl;
	public apiServerURL: UntypedFormControl;
	public username: UntypedFormControl;
	public password: UntypedFormControl;

	private passwordChanged: boolean;
	private passwordChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private messageService: MessageService) {

		super();
	}

	public ngOnInit(): void {
		this.buildForm();
	}

	public ngOnDestroy(): void {
		if (this.passwordChangedSub) {
			this.passwordChangedSub.unsubscribe();
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {
		this.enabled = undefined;
		this.serverURL = undefined;
		this.apiServerURL = undefined;
		this.username = undefined;
		this.password = undefined;

		let enabled = true;
		let serverURL = "";
		let apiServerURL = "";
		let username = "";
		let password = "";

		// Load in the settings from the input settings variable, and set them to the form controls.
		if (this.inputSettings) {
			enabled = this.inputSettings.Enabled;
			serverURL = this.inputSettings.ServerURL;
			apiServerURL = this.inputSettings.ApiServerURL;
			username = this.inputSettings.Username;
			password = (this.inputSettings.HasPassword ? this.hidePassword(this.inputSettings.Password) : "");
		}

		this.enabled = new UntypedFormControl(enabled);
		this.serverURL = new UntypedFormControl(serverURL);
		this.apiServerURL = new UntypedFormControl(apiServerURL);
		this.username = new UntypedFormControl(username);
		this.password = new UntypedFormControl(password);

		this.passwordChanged = false;
		this.passwordChangedSub = this.password.valueChanges.subscribe(() => {
			this.passwordChanged = true;
		});

		this.form = this.fb.group({
			enabled: this.enabled,
			serverURL: this.serverURL,
			apiServerURL: this.apiServerURL,
			username: this.username,
			password: this.password,
		},
		{
			validator: Validators.compose([
				this.noneOrAllSettings(),
			])
		});
	}

	public enableSave(): boolean {
		return !this.form.invalid;
	}

	public onSave(): void {
		const outputIntegrationTable = new IntegrationTable();
		const integrationSettings = this.getIntegrationSettings();
		outputIntegrationTable.Add(integrationSettings);
		this.configService.setIntegrationTable(outputIntegrationTable).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully saved Asset Panda default settings");
				this.form.markAsPristine();
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to save Asset Panda default settings");
			});
	}

	public get enableTestAssetPandaButton(): boolean {
		return (!!this.apiServerURL && !!this.apiServerURL.value
			&& !!this.username && !!this.username.value
			&& !!this.password && !!this.password.value);
	}

	public testConnection(): void {
		let password = "";
		if (this.passwordChanged) {
			password = StringUtils.ObfuscateString2(this.password.value);
		}
		else {
			password = this.inputSettings.Password;
		}
		this.configService.testAssetPandaConnection(this.apiServerURL.value, this.username.value, password).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully connected to Asset Panda");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to connect to Asset Panda");
			});
	}

	public onPasswordKeyDownHandler(event: any): void {
		if (!this.passwordChanged) {
			this.passwordChanged = true;

			this.password.setValue('');
			this.password.markAsDirty();
		}
	}

	public getIntegrationSettings(): Integration {
		const outputIntegration = new Integration(IntegrationNames.AssetPanda);
		outputIntegration.Enabled = this.enabled.value;
		outputIntegration.ServerURL = this.serverURL.value;
		outputIntegration.ApiServerURL = this.apiServerURL.value;
		outputIntegration.Username = this.username.value;
		if (this.passwordChanged) {
			outputIntegration.Password = StringUtils.ObfuscateString2(this.password.value);
		}
		else {
			outputIntegration.Password = this.inputSettings.Password;
		}

		return outputIntegration;
	}

	private noneOrAllSettings(): ValidatorFn {
		return () => {
			let b1 = (!!this.serverURL.value !== !!this.apiServerURL.value);
			let b2 = (!!this.apiServerURL.value !== !!this.username.value);
			let b3 = (!!this.username.value !== !!this.password.value);
			if (b1 || b2 || b3) {
				return {
					noneOrAllSettings: { valid: false }
				};
			}

			return null;
		}
	}

}
